import { WidgetId } from 'types/idTypes';

type ResizeShape = (width: number, height: number) => void;

/**
 * The Basic Shape widget has a resize handler that needs to be called when the widget is resized.
 * This class stores the resize handler so that it can be called from outside the widget.
 */
class ResponsiveShapeResize {
  private storedResizeFns: Record<WidgetId, ResizeShape> = {};
  private static instance: ResponsiveShapeResize;

  /**
   * Adds the resize function to the store for a specific widget
   *
   * @param widgetId
   * @param resizeFn
   *
   */
  addResizeFn(widgetId: WidgetId, resizeFn: ResizeShape) {
    if (!widgetId || !resizeFn) return;

    this.storedResizeFns[widgetId] = resizeFn;
  }

  /**
   * Clears the resize function from the store for a specific widget
   *
   * @param parentId
   */
  clear(widgetId: WidgetId) {
    if (!widgetId) return;

    delete this.storedResizeFns[widgetId];
  }

  /**
   * Calls the resize function for a specific widget
   * @param widgetId
   * @param width
   * @param height
   *
   */
  resize(widgetId: WidgetId, width: number, height: number) {
    if (!widgetId) return;
    if (typeof width !== 'number' || typeof height !== 'number') return;

    const resizeFn = this.storedResizeFns[widgetId];
    if (!resizeFn) {
      console.warn('No resize function found for widgetId', widgetId);
      return;
    }

    resizeFn(width, height);
  }

  /**
   * Returns the singleton instance
   * @returns ResponsiveShapeResize
   */
  public static getInstance(): ResponsiveShapeResize {
    if (!ResponsiveShapeResize.instance) {
      ResponsiveShapeResize.instance = new ResponsiveShapeResize();
    }

    return ResponsiveShapeResize.instance;
  }
}

export const responsiveShapeResize = ResponsiveShapeResize.getInstance();
export type ResponsiveShapeResizeType = ResponsiveShapeResize;
